import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshSubject = new BehaviorSubject<string | null>(null);

  constructor(private authService: AuthService, private router: Router) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isAuthUrl = req.url.includes('/api/v1/auth/login') ||
                      req.url.includes('/api/v1/auth/refresh') ||
                      req.url.includes('/api/v1/auth/logout') ||
                      req.url.includes('/api/v1/auth/primeiro-acesso') ||
                      req.url.includes('/api/v1/auth/aceitar-termos') ||
                      req.url.includes('/api/v1/auth/termos/');

    const token = this.authService.getToken();
    const request = isAuthUrl || !token
      ? req
      : req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401 && !isAuthUrl) {
          return this.handle401(req, next);
        }
        if (error.status === 403) {
          // TERMOS_PENDENTES: limpa sessão e redireciona para login sem exibir erro genérico
          // O login vai mostrar o painel de re-aceite de termos automaticamente
          if (error.error?.codigo === 'TERMOS_PENDENTES') {
            this.authService.clearAll();
            this.router.navigate(['/login']);
            return throwError(() => error);
          }
          // Other 403s (hierarchical access, permission denied) should NOT clear auth
        }
        return throwError(() => error);
      })
    );
  }

  private handle401(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.isRefreshing) {
      return this.refreshSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => next.handle(
          req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
        ))
      );
    }

    this.isRefreshing = true;
    this.refreshSubject.next(null);

    return this.authService.refreshAccessToken().pipe(
      switchMap(response => {
        this.isRefreshing = false;
        this.refreshSubject.next(response.accessToken);
        return next.handle(
          req.clone({ setHeaders: { Authorization: `Bearer ${response.accessToken}` } })
        );
      }),
      catchError(err => {
        this.isRefreshing = false;
        this.authService.clearAll();
        this.router.navigate(['/login']);
        return throwError(() => err);
      })
    );
  }
}
